/**
 * ReservationDetailView — the WIRED reservation detail body (SCRUM-335,
 * Step 6). A `modules/booking` feature (a user-facing action surface: the
 * lifecycle action cluster + tab scaffold), NOT a cross-BC widget. The only
 * cross-slice touch is the sanctioned barrel-only `usePropertyNameLookup()`
 * reference lookup from the `property` slice (a thin `id → name` map), mirroring
 * `ReservationsGrid`.
 *
 * **Composition (never refetches / re-maps here).**
 *   - `useReservationDetail(reservationId)` — PRIMARY state source. Renders the
 *     five states off the discriminated status union: `loading` (skeleton),
 *     `error` (inline message + Retry), `forbidden` (permission panel),
 *     `notFound` (not-found panel), `ready` (chrome).
 *   - `usePropertyNameLookup()` (property barrel) + `useReservationTypes()` —
 *     AUXILIARY: both degrade gracefully (short-id / neutral type label) when
 *     not `ready`; they never hard-block the detail.
 *   - `toReservationDetailView(dto, nameById, typeNameById)` — the pure DTO →
 *     view projection (nights, `modified`, `glyphKind`, source/type labels,
 *     Cents passthrough). The feature is a thin renderer over the view.
 *   - `useReservationLifecycle(reservationId)` — the grouped five-op mutation
 *     hook backing the FSM-gated action cluster.
 *
 * **Lifecycle actions (FSM-aware + per-slug-gated).** Only the actions the FSM
 * permits for the current status render (QUO→[confirm, hold]; HOLD→[confirm,
 * release]; BKD→[check-in, cancel]; CI→[check-out, cancel]; CO/CXL/HIST→none),
 * each wrapped in `<RequirePermission slug=…>` (confirm/hold =
 * `booking.reservations.write`; check-in/check-out =
 * `booking.reservations.operate`; cancel/release = `booking.reservations.cancel`).
 * HOLD "release" = `cancel.mutate({ pathway: null })`; BKD/CI "cancel" opens a
 * small pathway chooser (Hold-credit / Disburse / Cancellation-fee). Errors branch
 * on the tagged `LifecycleError.kind`: `conflict` → an `aria-live` refresh notice;
 * `illegal` → an unavailable-action notice; `forbidden`/`notFound` → an honest
 * message. On success → an `aria-live` confirmation. The status pill NEVER
 * optimistically flips — it re-renders from the invalidated detail refetch.
 *
 * **Six-tab scaffold (this ticket wires NONE of the bodies).** A WAI-ARIA
 * tablist (roving focus / arrow keys / `role=tab`/`tabpanel`/`aria-selected`)
 * that scrolls horizontally on mobile without page bleed or focus-trap. Every
 * panel is an honest placeholder — no fabricated rows/figures, no internal
 * identifiers in copy. Ledger → SCRUM-336; payments/history → SCRUM-338;
 * channel-notes → SCRUM-382; communication/tasks → BCs unbuilt.
 *
 * **Money is `Cents`.** The Total stat renders via `formatCents(cents, intl)` at
 * the display boundary — never a hand-rolled `/100`; a `null` legacy total is a
 * neutral placeholder, not `formatCents(0)`.
 *
 * **Tokens (ADR frontend-0002/0003, SCRUM-78/79).** Semantic `var(--token)` via
 * arbitrary-value classes only; no raw hex/rgba, no inline-style numeric
 * literals, no pixel-literal arbitrary values. Inline style is used only for
 * the prop-driven active/inactive tab toggle (ADR frontend-0002 exemption 1,
 * the `FilterChip` precedent).
 *
 * **A11y (WCAG 2.1 AA, FE rule 7).** Semantic HTML, keyboard-navigable tabs +
 * actions, `aria-live` mutation notices, status conveyed by text (never color
 * alone), ≥44px touch targets, focus-visible rings from the kit `<Button>`.
 */

import * as React from 'react';
import { useIntl } from 'react-intl';
import { Button, Card, Icon } from '@sparta/ui-kit';
import type { PermissionSlug } from '@artemis/contracts';
import { formatCents } from '../../../shared/formatters/entity-change/values.js';
import { RequirePermission } from '../../../shared/auth/RequirePermission.js';
import { usePropertyNameLookup } from '../../../modules/property/index.js';
import { useReservationDetail } from '../api/use-reservation-detail.js';
import {
  useReservationLifecycle,
  type LifecycleError,
  type UseReservationLifecycleResult,
} from '../api/use-reservation-lifecycle.js';
import { useReservationTypes } from '../api/use-reservation-types.js';
import {
  toReservationDetailView,
  type ReservationDetailView as ReservationDetailViewModel,
} from '../api/to-reservation-detail-view.js';
import { TypeIconBadge } from '../_components/TypeIconBadge.js';
import { ResStatusPill } from '../_components/ResStatusPill.js';
import { RsvStat } from '../_components/RsvStat.js';
import { HealthAgent } from '../_components/HealthAgent.js';
import { LedgerTab } from './LedgerTab.js';
import { PaymentsTab } from './PaymentsTab.js';
import { HistoryTab } from './HistoryTab.js';
import { ChannelNotesTab } from './ChannelNotesTab.js';

export interface ReservationDetailViewProps {
  /** The reservation to load (`/reservations/:id`). */
  readonly reservationId: string;
  /** Back affordance → return to the reservations list. */
  readonly onBack?: () => void;
}

export function ReservationDetailView({
  reservationId,
  onBack,
}: ReservationDetailViewProps): React.ReactElement {
  const detail = useReservationDetail(reservationId);
  const lifecycle = useReservationLifecycle(reservationId);
  const nameLookup = usePropertyNameLookup();
  const typesResult = useReservationTypes();

  const nameById = React.useMemo<ReadonlyMap<string, string>>(
    () => (nameLookup.status === 'ready' ? nameLookup.nameById : new Map()),
    [nameLookup],
  );

  const typeNameById = React.useMemo<ReadonlyMap<string, string>>(() => {
    if (typesResult.status !== 'ready') return new Map();
    // displayName ONLY — never surface a raw internalKey in copy.
    return new Map(typesResult.data.map((entry) => [entry.internalKey, entry.displayName]));
  }, [typesResult]);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pt-5 pb-20 sm:px-10 sm:pt-7">
      <BackLink onBack={onBack} />
      <DetailStateBody
        reservationId={reservationId}
        detail={detail}
        lifecycle={lifecycle}
        nameById={nameById}
        typeNameById={typeNameById}
      />
    </div>
  );
}

/* ── back link ─────────────────────────────────────────────────────────── */

function BackLink({ onBack }: { readonly onBack?: () => void }): React.ReactElement {
  return (
    <Button variant="quiet" size="sm" onClick={() => onBack?.()} className="mb-4 -ml-2">
      <Icon name="chevLeft" size={16} aria-hidden />
      <span className="ml-1">Reservations</span>
    </Button>
  );
}

/* ── state switch (loading / error / forbidden / notFound / ready) ───────── */

interface DetailStateBodyProps {
  readonly reservationId: string;
  readonly detail: ReturnType<typeof useReservationDetail>;
  readonly lifecycle: UseReservationLifecycleResult;
  readonly nameById: ReadonlyMap<string, string>;
  readonly typeNameById: ReadonlyMap<string, string>;
}

function DetailStateBody({
  reservationId,
  detail,
  lifecycle,
  nameById,
  typeNameById,
}: DetailStateBodyProps): React.ReactElement {
  if (detail.status === 'loading') {
    return <LoadingSkeleton />;
  }
  if (detail.status === 'forbidden') {
    return <ForbiddenPanel />;
  }
  if (detail.status === 'notFound') {
    return <NotFoundPanel />;
  }
  if (detail.status === 'error') {
    return <ErrorPanel onRetry={detail.refetch} />;
  }

  const view = toReservationDetailView(detail.data, nameById, typeNameById);
  return <ReadyBody view={view} lifecycle={lifecycle} reservationId={reservationId} />;
}

function LoadingSkeleton(): React.ReactElement {
  return (
    <Card padding="lg">
      <div aria-busy="true" aria-live="polite" data-testid="skeleton">
        <div className="flex items-center gap-3">
          <span
            className="inline-block h-10 w-10 rounded-md bg-[var(--bg-sunken)]"
            aria-hidden="true"
          />
          <span
            className="inline-block h-5 w-56 rounded bg-[var(--bg-sunken)]"
            aria-hidden="true"
          />
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className="inline-block h-10 w-full rounded bg-[var(--bg-sunken)]"
              aria-hidden="true"
            />
          ))}
        </div>
      </div>
      <span className="sr-only">Loading reservation…</span>
    </Card>
  );
}

function ForbiddenPanel(): React.ReactElement {
  return (
    <Card padding="lg" className="text-center">
      <h1 className="mb-1 text-base font-semibold text-[color:var(--fg-primary)]">
        You don’t have access to this reservation.
      </h1>
      <p className="text-sm text-[color:var(--fg-secondary)]">
        Ask an administrator to grant you the required permission.
      </p>
    </Card>
  );
}

function NotFoundPanel(): React.ReactElement {
  return (
    <Card padding="lg" className="text-center">
      <h1 className="mb-1 text-base font-semibold text-[color:var(--fg-primary)]">
        Reservation not found.
      </h1>
      <p className="text-sm text-[color:var(--fg-secondary)]">
        It may have been removed, or you may be looking at the wrong workspace.
      </p>
    </Card>
  );
}

function ErrorPanel({ onRetry }: { readonly onRetry: () => void }): React.ReactElement {
  return (
    <Card
      padding="lg"
      className="flex items-center justify-between gap-4"
      role="alert"
      aria-live="assertive"
    >
      <div>
        <h1 className="mb-1 text-base font-semibold text-[color:var(--fg-primary)]">
          Couldn’t load this reservation.
        </h1>
        <p className="text-sm text-[color:var(--fg-secondary)]">
          Check your connection and try again.
        </p>
      </div>
      <Button variant="secondary" onClick={onRetry}>
        Retry
      </Button>
    </Card>
  );
}

/* ── ready body: header chrome + stats + actions + tabs ──────────────────── */

interface ReadyBodyProps {
  readonly view: ReservationDetailViewModel;
  readonly lifecycle: UseReservationLifecycleResult;
  readonly reservationId: string;
}

function ReadyBody({ view, lifecycle, reservationId }: ReadyBodyProps): React.ReactElement {
  return (
    <div className="flex flex-col gap-4">
      {/* Hero card (identity + stat grid) beside the HealthAgent — design 183-202.
          Stats live INSIDE the hero card, not a separate sibling card. */}
      <HeaderRow view={view} />
      <ActionCluster status={view.status} lifecycle={lifecycle} />
      <TabScaffold reservationId={reservationId} />
    </div>
  );
}

/* ── header row: identity card (flex-grow) | HealthAgent (~260px) ────────── */

function HeaderRow({ view }: { readonly view: ReservationDetailViewModel }): React.ReactElement {
  // Mono subtitle: shortId · type · source. Drop a segment gracefully when it
  // is unresolved (never a raw UPPER_SNAKE key — the mapper already resolves a
  // neutral "—" for an unknown type key, which we treat as "drop the segment").
  const shortId = view.id.split('-')[0] ?? view.id;
  const subtitleSegments = [
    shortId,
    view.typeDisplayName === '—' ? null : view.typeDisplayName,
    view.sourceLabel,
  ].filter((segment): segment is string => segment != null && segment.length > 0);

  return (
    // Content-driven side-by-side: hero + HealthAgent sit adjacent once both fit
    // (~md), stacking below — not the design's fixed width, but a lint-safe
    // breakpoint far closer to the content threshold than the old lg: (which
    // stacked the widget full-width across the whole tablet range).
    <div className="flex flex-col gap-4 md:flex-row md:items-stretch">
      <Card padding="lg" className="flex-1">
        <div className="flex items-center gap-3">
          <span className="shrink-0">
            <TypeIconBadge
              stayKind={glyphStayKindFor(view.glyphKind)}
              reservationTypeKey={view.reservationTypeKey}
              bookingChannel={bookingChannelFor(view.glyphKind)}
            />
          </span>
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h1 className="m-0 truncate text-2xl font-bold tracking-[-0.025em] text-[color:var(--fg-primary)]">
                {view.guestDisplayName ?? 'No name on file'}
              </h1>
              <ResStatusPill status={view.status} modified={view.modified} />
            </div>
            <p className="m-0 truncate font-mono text-xs text-[color:var(--fg-tertiary)]">
              {subtitleSegments.join(' · ')}
            </p>
          </div>
        </div>
        {/* Stat grid inside the hero card (design 195-200), separated from the
            identity block by a small gap. */}
        <div className="mt-4">
          <StatStrip view={view} />
        </div>
      </Card>
      <div className="w-full md:w-64 md:shrink-0">
        <HealthAgent />
      </div>
    </div>
  );
}

/* ── stat strip: Property / Stay / Guests / Total ────────────────────────── */

function StatStrip({ view }: { readonly view: ReservationDetailViewModel }): React.ReactElement {
  const intl = useIntl();
  const totalLabel = view.grandTotalCents === null ? '—' : formatCents(view.grandTotalCents, intl);
  // Calendar LocalDates → "Jun 12" (MON DD), design parity (reservations.jsx:197).
  // `timeZone: 'UTC'` avoids the date-only off-by-one (mirrors the grid StayCell).
  const fmtDate = (iso: string): string =>
    intl.formatDate(iso, { month: 'short', day: '2-digit', timeZone: 'UTC' });
  const stayValue = `${fmtDate(view.arrivalDate)}–${fmtDate(view.departureDate)}`;
  const nightsLabel = `${view.nights} ${view.nights === 1 ? 'night' : 'nights'}`;

  return (
    // No Card wrapper — StatStrip renders INSIDE the hero card (design 195-200).
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <RsvStat label="Property" value={view.propertyName} />
      <RsvStat
        label="Stay"
        value={
          <span className="flex flex-col">
            <span className="tabular-nums">{stayValue}</span>
            <span className="text-xs font-normal text-[color:var(--fg-tertiary)]">
              {nightsLabel}
            </span>
          </span>
        }
      />
      <RsvStat label="Guests" value={view.guestsLabel} />
      <RsvStat label="Total" value={<span className="tabular-nums">{totalLabel}</span>} />
    </div>
  );
}

/* ── lifecycle action cluster (FSM-aware + per-slug-gated) ───────────────── */

/** The FE affordance FSM: which actions each status permits. The server stays
 *  the authority (422 on any illegal edge) — this is UI affordance logic
 *  (like `statusLabelFor`), a documented mirror of the domain FSM with NO wire
 *  contract to gate against. */
type LifecycleActionId = 'confirm' | 'hold' | 'checkIn' | 'checkOut' | 'cancel' | 'release';

const ACTIONS_BY_STATUS: Readonly<Record<string, readonly LifecycleActionId[]>> = {
  QUO: ['confirm', 'hold'],
  HOLD: ['confirm', 'release'],
  BKD: ['checkIn', 'cancel'],
  CI: ['checkOut', 'cancel'],
  CO: [],
  CXL: [],
  HIST: [],
};

/** action → (label, slug, kit variant). */
const ACTION_META: Readonly<
  Record<
    LifecycleActionId,
    {
      readonly label: string;
      readonly slug: PermissionSlug;
      readonly variant: 'primary' | 'secondary' | 'danger';
    }
  >
> = {
  confirm: { label: 'Confirm', slug: 'booking.reservations.write', variant: 'primary' },
  hold: { label: 'Hold', slug: 'booking.reservations.write', variant: 'secondary' },
  checkIn: { label: 'Check-in', slug: 'booking.reservations.operate', variant: 'primary' },
  checkOut: { label: 'Check-out', slug: 'booking.reservations.operate', variant: 'primary' },
  cancel: { label: 'Cancel', slug: 'booking.reservations.cancel', variant: 'danger' },
  release: { label: 'Release hold', slug: 'booking.reservations.cancel', variant: 'secondary' },
};

/** The disbursement pathways the cancel chooser offers (BKD/CI cancel). The
 *  domain VO enforces this closed set (422 otherwise); `null` = HOLD release. */
const CANCEL_PATHWAYS: readonly { readonly id: string; readonly label: string }[] = [
  { id: 'Hold-credit', label: 'Hold credit' },
  { id: 'Disburse', label: 'Disburse' },
  { id: 'Cancellation-fee', label: 'Cancellation fee' },
];

interface ActionClusterProps {
  readonly status: string;
  readonly lifecycle: UseReservationLifecycleResult;
}

function ActionCluster({ status, lifecycle }: ActionClusterProps): React.ReactElement | null {
  const [chooserOpen, setChooserOpen] = React.useState(false);
  const actions = ACTIONS_BY_STATUS[status] ?? [];

  // Aggregate the tagged error kind across every op (only one is ever in an
  // error state at a time — the last-clicked action). Success is likewise
  // aggregated so the confirmation notice appears for whichever op resolved.
  const errorKind = firstErrorKind(lifecycle);
  const anySuccess = anyOpSucceeded(lifecycle);

  if (actions.length === 0) {
    // Terminal state (CO/CXL/HIST): no actions. Still surface a lingering
    // notice (e.g. a conflict from a just-completed transition) via aria-live.
    return <ActionNotices errorKind={errorKind} anySuccess={anySuccess} />;
  }

  const fire = (id: LifecycleActionId): void => {
    switch (id) {
      case 'confirm':
        lifecycle.confirm.mutate();
        break;
      case 'hold':
        lifecycle.hold.mutate();
        break;
      case 'checkIn':
        lifecycle.checkIn.mutate();
        break;
      case 'checkOut':
        lifecycle.checkOut.mutate();
        break;
      case 'release':
        // HOLD release = cancel with a null pathway.
        lifecycle.cancel.mutate({ pathway: null });
        break;
      case 'cancel':
        // BKD/CI cancel opens the pathway chooser first.
        setChooserOpen(true);
        break;
    }
  };

  const cancelWithPathway = (pathway: string): void => {
    lifecycle.cancel.mutate({ pathway });
    setChooserOpen(false);
  };

  // codex F-1: lock the WHOLE cluster while any op is in flight — the actions
  // are mutually exclusive, so leaving siblings enabled during a pending
  // request would let a user queue a second, conflicting transition before the
  // success refetch lands (e.g. check-in then cancel-from-CI on two clicks).
  const busy = anyOpPending(lifecycle);

  return (
    <Card padding="md">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="mr-2 text-xs font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]">
          Actions
        </h2>
        {actions.map((id) => {
          const meta = ACTION_META[id];
          return (
            <RequirePermission key={id} slug={meta.slug} fallback={null}>
              <Button variant={meta.variant} size="sm" onClick={() => fire(id)} disabled={busy}>
                {meta.label}
              </Button>
            </RequirePermission>
          );
        })}
      </div>

      {chooserOpen ? (
        <CancelPathwayChooser
          onChoose={cancelWithPathway}
          onDismiss={() => setChooserOpen(false)}
          disabled={busy}
        />
      ) : null}

      <ActionNotices errorKind={errorKind} anySuccess={anySuccess} />
    </Card>
  );
}

/** The BKD/CI cancel pathway chooser — a small inline disclosure (the kit has
 *  no Dialog primitive). Keyboard-accessible: `role="group"` labelled by its
 *  heading; each choice is a kit `<Button>`; Escape dismisses. */
function CancelPathwayChooser({
  onChoose,
  onDismiss,
  disabled,
}: {
  readonly onChoose: (pathway: string) => void;
  readonly onDismiss: () => void;
  readonly disabled: boolean;
}): React.ReactElement {
  const headingId = React.useId();
  return (
    <div
      role="group"
      aria-labelledby={headingId}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onDismiss();
      }}
      className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-[var(--border-subtle)] bg-[var(--bg-sunken)] p-3"
    >
      <p
        id={headingId}
        className="mr-1 w-full text-sm font-semibold text-[color:var(--fg-primary)] sm:w-auto"
      >
        How should this cancellation be settled?
      </p>
      {CANCEL_PATHWAYS.map((pathway) => (
        <Button
          key={pathway.id}
          variant="secondary"
          size="sm"
          onClick={() => onChoose(pathway.id)}
          disabled={disabled}
        >
          {pathway.label}
        </Button>
      ))}
      <Button variant="quiet" size="sm" onClick={onDismiss}>
        Dismiss
      </Button>
    </div>
  );
}

/** The aria-live mutation notices (conflict / illegal / forbidden / notFound /
 *  generic error / success). Rendered in a polite live region so a screen
 *  reader announces the outcome without stealing focus. */
function ActionNotices({
  errorKind,
  anySuccess,
}: {
  readonly errorKind: LifecycleError['kind'] | null;
  readonly anySuccess: boolean;
}): React.ReactElement {
  const message = errorKind !== null ? errorMessageFor(errorKind) : null;
  return (
    <div aria-live="polite" className="mt-2 empty:hidden">
      {message !== null ? (
        <p role="status" className="flex items-center gap-2 text-sm text-[color:var(--danger-500)]">
          <Icon name="alertTriangle" size={14} aria-hidden />
          {message}
        </p>
      ) : anySuccess ? (
        <p
          role="status"
          className="flex items-center gap-2 text-sm text-[color:var(--success-500)]"
        >
          <Icon name="check" size={14} aria-hidden />
          Done — the reservation has been updated.
        </p>
      ) : null}
    </div>
  );
}

function errorMessageFor(kind: LifecycleError['kind']): string {
  switch (kind) {
    case 'conflict':
      return 'This reservation changed since you loaded it — refresh and try again.';
    case 'illegal':
      return 'That action isn’t available for this reservation’s current status.';
    case 'forbidden':
      return 'You don’t have permission to perform that action.';
    case 'notFound':
      return 'This reservation could not be found.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

/* ── lifecycle-op aggregation helpers ────────────────────────────────────── */

/** Every op in the grouped hook, in a stable order for aggregation. */
function allOps(
  lifecycle: UseReservationLifecycleResult,
): readonly UseReservationLifecycleResult[keyof UseReservationLifecycleResult][] {
  return [
    lifecycle.confirm,
    lifecycle.hold,
    lifecycle.checkIn,
    lifecycle.checkOut,
    lifecycle.cancel,
  ];
}

/** The first errored op's tagged `kind` (only one op is ever active at once). */
function firstErrorKind(lifecycle: UseReservationLifecycleResult): LifecycleError['kind'] | null {
  for (const op of allOps(lifecycle)) {
    if (op.isError && op.error != null) {
      return op.error.kind;
    }
  }
  return null;
}

/** True when any op resolved successfully (drives the success confirmation). */
function anyOpSucceeded(lifecycle: UseReservationLifecycleResult): boolean {
  return allOps(lifecycle).some((op) => op.status === 'success');
}

/** True while ANY lifecycle op is in flight. The whole action cluster (and the
 *  cancel-pathway chooser) locks on this so a user cannot dispatch a second,
 *  conflicting transition on the same reservation before the success refetch
 *  lands — the lifecycle actions are mutually exclusive (codex F-1). */
function anyOpPending(lifecycle: UseReservationLifecycleResult): boolean {
  return allOps(lifecycle).some((op) => op.isPending);
}

/* ── six-tab scaffold (WAI-ARIA tabs; bodies are honest placeholders) ────── */

interface TabDef {
  readonly id: string;
  readonly label: string;
}

const TABS: readonly TabDef[] = [
  { id: 'ledger', label: 'ledger' },
  { id: 'payments', label: 'payments' },
  { id: 'channel-notes', label: 'channel notes' },
  { id: 'communication', label: 'communication' },
  { id: 'tasks', label: 'tasks' },
  { id: 'history', label: 'history' },
];

function TabScaffold({ reservationId }: { readonly reservationId: string }): React.ReactElement {
  const [active, setActive] = React.useState(0);
  const idBase = React.useId();
  const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  const focusTab = (index: number): void => {
    const clamped = (index + TABS.length) % TABS.length;
    setActive(clamped);
    tabRefs.current[clamped]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent, index: number): void => {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        focusTab(index + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        focusTab(index - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusTab(0);
        break;
      case 'End':
        e.preventDefault();
        focusTab(TABS.length - 1);
        break;
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Standalone pill track (design reservations.jsx:206): a rounded,
          bordered segmented control, horizontally scrollable on mobile
          (`overflow-x-auto` + `max-w-full`), no focus-trap (roving tabindex
          keeps a single stop). */}
      <div className="max-w-full overflow-x-auto">
        <div
          role="tablist"
          aria-label="Reservation detail sections"
          className="flex w-fit max-w-full items-center gap-1 rounded-full border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-1"
        >
          {TABS.map((tab, index) => {
            const isActive = index === active;
            return (
              // eslint-disable-next-line @sparta/no-hand-rolled-button -- WAI-ARIA tab: `role="tab"` + roving tabindex must live on a native <button>; kit <Button> has no tab variant / would shadow the role (mirrors CompanyDetailPage's tablist).
              <button
                key={tab.id}
                ref={(el) => {
                  tabRefs.current[index] = el;
                }}
                type="button"
                role="tab"
                id={`${idBase}-tab-${tab.id}`}
                aria-selected={isActive}
                aria-controls={`${idBase}-panel-${tab.id}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActive(index)}
                onKeyDown={(e) => onKeyDown(e, index)}
                className="inline-flex min-h-11 shrink-0 cursor-pointer items-center whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold capitalize transition-colors"
                // ADR frontend-0002 exemption 1: background + foreground are
                // prop-driven (the active tab toggles both tokens together).
                style={{
                  background: isActive ? 'var(--fg-primary)' : 'transparent',
                  color: isActive ? 'var(--bg-surface)' : 'var(--fg-secondary)',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Panels — in their own card (design: pill track + separate panel card).
          Only the active panel renders content, so its p-6 supplies the padding;
          no per-panel border-t is needed (the Card frames the group). */}
      <Card padding="none" className="max-w-full">
        {TABS.map((tab, index) => (
          <div
            key={tab.id}
            role="tabpanel"
            id={`${idBase}-panel-${tab.id}`}
            aria-labelledby={`${idBase}-tab-${tab.id}`}
            hidden={index !== active}
            tabIndex={0}
            className="p-6"
          >
            {index === active ? (
              // SCRUM-336/338/382: ledger, payments, history, and channel-notes
              // panels are wired. The remaining two (communication / tasks) stay
              // honest placeholders — their bounded contexts are not yet built.
              // Lazy-mount is preserved: only the ACTIVE panel renders (its hooks
              // fire only when its tab is active).
              tab.id === 'ledger' ? (
                <LedgerTab reservationId={reservationId} />
              ) : tab.id === 'payments' ? (
                <PaymentsTab reservationId={reservationId} />
              ) : tab.id === 'history' ? (
                <HistoryTab reservationId={reservationId} />
              ) : tab.id === 'channel-notes' ? (
                <ChannelNotesTab reservationId={reservationId} />
              ) : (
                <TabPlaceholder label={tab.label} />
              )
            ) : null}
          </div>
        ))}
      </Card>
    </div>
  );
}

/** An honest in-panel empty state — no fabricated rows/figures, no internal
 *  identifiers or ticket numbers in copy. */
function TabPlaceholder({ label }: { readonly label: string }): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <span
        aria-hidden="true"
        className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-sunken)] text-[color:var(--fg-tertiary)]"
      >
        <Icon name="clock" size={20} />
      </span>
      <p className="text-sm font-semibold capitalize text-[color:var(--fg-primary)]">{label}</p>
      <p className="mt-1 text-sm text-[color:var(--fg-secondary)]">Not connected yet.</p>
    </div>
  );
}

/* ── glyph-kind → TypeIconBadge inputs (mirrors ReservationsGrid) ─────────── */

/**
 * The view carries the pre-computed `glyphKind` bucket, but `TypeIconBadge`
 * re-derives it from `(stayKind, reservationTypeKey, bookingChannel)`. We feed
 * the badge the canonical `stayKind` / `bookingChannel` inputs that reproduce
 * the SAME bucket via `glyphKindFor` — keeping the badge's derivation the
 * single source of the glyph (the `ReservationsGrid` precedent).
 */
function glyphStayKindFor(kind: ReservationDetailViewModel['glyphKind']): string {
  switch (kind) {
    case 'ownerBlock':
      return 'NonStayBlock';
    case 'ownerStay':
      return 'OwnerStay';
    case 'directGuest':
    case 'channelGuest':
      return 'PayingGuest';
    default:
      return '';
  }
}

/** A non-empty channel token for the `channelGuest` bucket only. */
function bookingChannelFor(kind: ReservationDetailViewModel['glyphKind']): string | null {
  return kind === 'channelGuest' ? 'channel' : null;
}
